import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

const STORAGE_KEY = 'nisanShopCart_v1';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const addItem = (product, quantity = 1, personalization = null) => {
    setItems(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i =>
          i.productId === product.id
            ? {
                ...i,
                quantity: Math.min(300, i.quantity + quantity),
                // If new personalization provided, override existing
                personalization: personalization ?? i.personalization,
              }
            : i
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          slug: product.slug,
          name: product.name,
          image: (product.images || [])[0] || '',
          price: product.price,
          quantity,
          personalization,
        },
      ];
    });
  };

  const updatePersonalization = (productId, personalization) => {
    setItems(prev => prev.map(i => (i.productId === productId ? { ...i, personalization } : i)));
  };

  const updateQuantity = (productId, quantity) => {
    const q = Math.max(1, Math.min(300, quantity));
    setItems(prev => prev.map(i => (i.productId === productId ? { ...i, quantity: q } : i)));
  };

  const removeItem = (productId) => {
    setItems(prev => prev.filter(i => i.productId !== productId));
  };

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const FREE_SHIPPING = 500;
  const shipping = subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : 59.9;
  const total = subtotal + shipping;

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        subtotal,
        shipping,
        total,
        freeShippingThreshold: FREE_SHIPPING,
        addItem,
        updateQuantity,
        updatePersonalization,
        removeItem,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
